import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Button, InlineNotification, Tile } from '@carbon/react';
import { Edit, Maximize } from '@carbon/react/icons';
import { debounce } from 'lodash-es';
import {
  AggregationMetrics,
  WellProductionRecordResponse,
} from 'types/api-responses';
import { MineralType } from 'types/minerals';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  DAILY_GRANULARITY,
  MONTHLY_GRANULARITY,
  NO_VERSIONS,
} from './production-request-params';
import {
  useBulkForecastMetric,
  useForecastMetrics,
  toProductionSourceParam,
  useProduction,
  useVolumeDisplayMode,
  useVolumeMetric,
} from './hooks';
import { selectedDcaScenarioAtom, wellboresMutationAtom } from './atoms';
import {
  forecastEurMetrics,
  selectedForecastEurAtom,
} from './selected-forecast-eur';
import {
  type ForecastData,
  ProductionRecordSearchParams,
  VersionsBySource,
  WellVersions,
} from './types';
import { DeclineCurveAnalysisTable } from './decline-curve-analysis/decline-curve-analysis-table';
import { WellAnalysisChartPanel } from './well-analysis-chart-panel';
import { WellAnalysisChartTearsheet } from './well-analysis-chart-tearsheet';
import { WellAnalysisEurSummary } from './well-analysis-eur-summary';
import { WellDcaModal } from './well-dca-modal';
import { buildDcaForecastRows } from './dca-forecast-rows';
import { forecastExportValues } from './forecast-export-values';
import style from './well-analysis-content.module.scss';
import {
  isDailyProductionSource,
  PRODUCTION_DATA_SOURCE_LABELS,
} from 'components/production-source-dropdown';
import type {
  DcaScenariosResponse,
  ProductionDataSource,
} from 'types/api-responses';

const EVAL_VOLUMES_URL = '/queries/reservoir/eval_volumes';
const BULK_FORECASTS_URL = '/queries/reservoir/bulk_forecasts';

/** The sources shown when none are given. */
const DEFAULT_SOURCES: ProductionDataSource[] = ['ihs'];

/** Every stream shown when the section opens. */
const DEFAULT_STREAMS: MineralType[] = ['oil', 'gas', 'water'];

/**
 * Canvas height for the chart in the section. It fills the width rather than
 * keeping the 2:1 shape the other charts have, so a wide screen does not give
 * it more height than the section can hold.
 */
const SECTION_CHART_HEIGHT = 768;

/**
 * Whether two forecast ids are the same one.
 *
 * The APIs are inconsistent about the type: the versions list returns numbers
 * and the metrics come back as strings for the same forecast, so comparing
 * them directly misses the match.
 *
 * @param {string | number | null | undefined} a - One id.
 * @param {string | number | null | undefined} b - The other.
 * @return {boolean} True when both are set and equal.
 */
const sameId = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
): boolean => {
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }

  return String(a) === String(b);
};

/** Ratio metrics the figures need on top of the volumes. */
const RATIO_METRICS = [
  'total_gor',
  'total_watercut',
  'total_wgr',
  'total_wor',
  'total_cgr',
] as const;

/** The chart's other series types, which the volumes chart does not draw. */
const EMPTY_SERIES = {
  cumulative: [],
  ratio: [],
  materialBalanceTime: [],
};

/**
 * Turns a production response into chart series, one per selected version, in
 * the order the versions were selected.
 *
 * @param {WellProductionRecordResponse[string] | undefined} records - The well's production for each version.
 * @param {WellVersions} selectedVersions - The selected versions, which name the series.
 * @param {object} options - What the series are.
 * @param {ProductionDataSource} options.source - The source the production was read from.
 * @param {boolean} options.namePrefix - Whether to start each name with the source, for when more than one source is shown.
 * @param {boolean} options.isDailyRate - Whether the volumes are one row per day.
 * @return {Array} One series per version.
 */
const toProductionSeries = (
  records: WellProductionRecordResponse[string] | undefined,
  selectedVersions: WellVersions,
  {
    source,
    namePrefix,
    isDailyRate,
  }: { source: ProductionDataSource; namePrefix: boolean; isDailyRate: boolean }
) => {
  if (selectedVersions.length === 0) return [];

  return (records ?? []).map((item) => {
    const label =
      selectedVersions.find(
        (version) => version.versionId === item.productionVolumeVersionId
      )?.label ?? '';

    return {
      id: `record-${source}-${item.productionVolumeVersionId}`,
      source,
      versionId: item.productionVolumeVersionId,
      name: namePrefix
        ? `${PRODUCTION_DATA_SOURCE_LABELS[source]}: ${label}`
        : label,
      volume: item.volumes,
      isDailyRate,
    };
  });
};

export interface WellAnalysisContentProps {
  uwi: string;
  wellName?: string;
  /** IHS production. */
  recordHref: string;
  /** Daily production; absent where the well response doesn't link it. */
  dailyRecordHref?: string;
  /**
   * The sources to show, in order. The first is the chart's own production
   * line, which the figures and the DCA Forecast table follow. Defaults to IHS.
   */
  productionSources?: ProductionDataSource[];
  /** The production versions selected for each source. */
  selectedRecordVersions: VersionsBySource;
  selectedForecastVersions: WellVersions;
  selectedInsightForecastVersions: WellVersions;
  /** The version selectors, repeated inside the expanded-chart tearsheet. */
  versionSelectors: ReactNode;
  /** The DCA scenario picked on the page. The DCA editor opens on it. */
  dcaScenario?: DcaScenariosResponse | null;
}

/**
 * The body of the Well Analysis section: the volumes chart, the production and
 * forecast figures, and the DCA Forecast table for the forecast on the chart.
 *
 * It owns the requests the whole section shares: production volumes, the
 * selected forecasts' volumes and parameters, and the aggregated metrics.
 * Expanding the chart or opening the DCA editor does not request them again.
 *
 * @param {WellAnalysisContentProps} props - The well, where its data lives, and the selected versions.
 * @return {JSX.Element} The section body.
 */
export const WellAnalysisContent = ({
  uwi,
  wellName,
  recordHref,
  dailyRecordHref,
  productionSources = DEFAULT_SOURCES,
  selectedRecordVersions,
  selectedForecastVersions,
  selectedInsightForecastVersions,
  versionSelectors,
  dcaScenario,
}: WellAnalysisContentProps) => {
  const hasDailySource = productionSources.some(isDailyProductionSource);
  const primarySource = productionSources[0] ?? 'ihs';
  const [selectedStreams, setSelectedStreams] =
    useState<MineralType[]>(DEFAULT_STREAMS);
  // Adding the first daily source, or removing the last one, resets the
  // display to its default.
  const [volumeDisplayMode, setVolumeDisplayMode] =
    useVolumeDisplayMode(hasDailySource);
  const [chartExpanded, setChartExpanded] = useState(false);
  const [dcaOpen, setDcaOpen] = useState(false);
  const setSelectedDcaScenario = useSetAtom(selectedDcaScenarioAtom);
  const wellboresData = useAtomValue(wellboresMutationAtom);
  const setSelectedForecastEur = useSetAtom(selectedForecastEurAtom);

  // Whether the chart draws daily production one row per day.
  const showDailyRows = hasDailySource && volumeDisplayMode === 'daily';

  /**
   * The versions selected for a source, or none when it isn't shown.
   *
   * @param {ProductionDataSource} source - The source.
   * @return {WellVersions} Its selected versions.
   */
  const shownVersions = (source: ProductionDataSource) =>
    (productionSources.includes(source) && selectedRecordVersions[source]) ||
    NO_VERSIONS;
  const ihsVersions = shownVersions('ihs');
  const dailyVersions = shownVersions('scada');
  const dailyHref = dailyRecordHref ?? recordHref;

  // Monthly production: the IHS records, and daily production summed by month.
  // The DCA Forecast table always reads these, since it works month by month.
  const {
    data: ihsRecords,
    isLoading: isIhsRecordsLoading,
    error: ihsRecordsError,
  } = useProduction<WellProductionRecordResponse, ProductionRecordSearchParams>(
    recordHref,
    uwi,
    ihsVersions,
    'productionVolumes',
    800
  );
  const {
    data: dailyMonthlyRecords,
    isLoading: isDailyMonthlyRecordsLoading,
    error: dailyMonthlyRecordsError,
  } = useProduction<WellProductionRecordResponse, ProductionRecordSearchParams>(
    dailyHref,
    uwi,
    dailyVersions,
    'dailyProduction',
    800,
    MONTHLY_GRANULARITY
  );

  // Daily production one row per day, fetched only while the chart shows it.
  const {
    data: dailyRecords,
    isLoading: isDailyRecordsLoading,
    error: dailyRecordsError,
  } = useProduction<WellProductionRecordResponse, ProductionRecordSearchParams>(
    dailyHref,
    uwi,
    showDailyRows ? dailyVersions : NO_VERSIONS,
    'dailyProduction',
    800,
    DAILY_GRANULARITY
  );

  const primaryVersions = shownVersions(primarySource);
  const metricRecordVersionIds = useMemo(
    () => primaryVersions.map((v) => v.versionId),
    [primaryVersions]
  );
  // The figures read the same production as the chart's own line.
  const metricSourceParam = useMemo(
    () => toProductionSourceParam(primarySource),
    [primarySource]
  );

  const {
    data: bulkForecasts,
    loading: isBulkForecastsLoading,
    error: bulkForecastsError,
    setBulkForecastMetricResponse,
  } = useBulkForecastMetric();

  const {
    data: recordMetric,
    loading: isRecordMetricLoading,
    setVolumeMetricResponse: setRecordMetricResponse,
  } = useVolumeMetric();

  const debouncedSetBulkForecasts = useMemo(
    () => debounce(setBulkForecastMetricResponse, 800),
    [setBulkForecastMetricResponse]
  );
  const debouncedSetRecordMetric = useMemo(
    () => debounce(setRecordMetricResponse, 800),
    [setRecordMetricResponse]
  );

  const hasForecastSelection =
    selectedForecastVersions.length > 0 ||
    selectedInsightForecastVersions.length > 0;

  // The forecasts' volumes and decline parameters, which both the chart and the
  // DCA Forecast table read.
  useEffect(() => {
    if (!hasForecastSelection || !uwi) return;

    debouncedSetBulkForecasts({
      url: BULK_FORECASTS_URL,
      type: 'POST',
      data: {
        query: {
          bulkForecasts: {
            legacyForecasts:
              selectedForecastVersions.length > 0
                ? [
                    {
                      wells: [{ uwi }],
                      versionIds: selectedForecastVersions.map(
                        (v) => v.versionId
                      ),
                    },
                  ]
                : undefined,
            wellDcaForecasts:
              selectedInsightForecastVersions.length > 0
                ? {
                    wellDcaForecastIds: selectedInsightForecastVersions.map(
                      (v) => v.versionId
                    ),
                  }
                : undefined,
          },
        },
      },
    });
  }, [
    debouncedSetBulkForecasts,
    uwi,
    hasForecastSelection,
    selectedForecastVersions,
    selectedInsightForecastVersions,
  ]);

  // EUR, remaining and the ratios, with a query per selected forecast.
  const forecastMetricQueries = useMemo(() => {
    if (primaryVersions.length === 0 || !uwi) return [];

    const forecasts = [
      ...selectedForecastVersions.map((v) => ({
        wells: [{ uwi }],
        versionIds: [v.versionId],
      })),
      ...selectedInsightForecastVersions.map((v) => ({
        wellDcaForecastIds: [v.versionId],
      })),
    ];

    return forecasts.map((forecast) => ({
      ...metricSourceParam,
      productionVolumes: [{ uwis: [uwi], versionIds: metricRecordVersionIds }],
      pdpForecast: [forecast],
      responseConfig: {
        aggregationMetricsToInclude: [
          'eur',
          'recovered',
          'remaining',
          ...RATIO_METRICS,
        ],
      },
    }));
  }, [
    uwi,
    selectedForecastVersions,
    primaryVersions,
    selectedInsightForecastVersions,
    metricRecordVersionIds,
    metricSourceParam,
  ]);

  const { entries: forecastMetricEntries, loading: isForecastMetricLoading } =
    useForecastMetrics(uwi, forecastMetricQueries);

  // What the well actually produced, which is what the Recovered figures show
  // whichever forecast is selected.
  useEffect(() => {
    if (primaryVersions.length === 0 || !uwi) return;

    debouncedSetRecordMetric({
      url: EVAL_VOLUMES_URL,
      type: 'POST',
      data: {
        query: {
          ...metricSourceParam,
          productionVolumes: [
            { uwis: [uwi], versionIds: metricRecordVersionIds },
          ],
          responseConfig: {
            aggregationMetricsToInclude: ['recovered', ...RATIO_METRICS],
          },
        },
      },
    });
  }, [
    debouncedSetRecordMetric,
    uwi,
    primaryVersions,
    metricRecordVersionIds,
    metricSourceParam,
  ]);

  const isLoading =
    (ihsVersions.length > 0 && isIhsRecordsLoading) ||
    (dailyVersions.length > 0 && isDailyMonthlyRecordsLoading) ||
    (showDailyRows && dailyVersions.length > 0 && isDailyRecordsLoading) ||
    isForecastMetricLoading ||
    isRecordMetricLoading ||
    isBulkForecastsLoading;
  const productionError =
    (ihsVersions.length > 0 ? ihsRecordsError : undefined) ||
    (dailyVersions.length > 0 ? dailyMonthlyRecordsError : undefined) ||
    (showDailyRows && dailyVersions.length > 0 ? dailyRecordsError : undefined);

  // ---------------------------------------------------------------------
  // Chart series
  // ---------------------------------------------------------------------

  /**
   * Monthly production for each source in order, and each source's versions in
   * the order they were selected.
   */
  const monthlyProductionSeries = useMemo(
    () =>
      productionSources.flatMap((source) => {
        const isDaily = isDailyProductionSource(source);
        if (isDaily ? dailyMonthlyRecordsError : ihsRecordsError) return [];

        return toProductionSeries(
          isDaily ? dailyMonthlyRecords : ihsRecords,
          isDaily ? dailyVersions : ihsVersions,
          {
            source,
            namePrefix: productionSources.length > 1,
            isDailyRate: false,
          }
        );
      }),
    [
      productionSources,
      ihsRecords,
      ihsRecordsError,
      ihsVersions,
      dailyMonthlyRecords,
      dailyMonthlyRecordsError,
      dailyVersions,
    ]
  );

  /**
   * The production the chart draws: the monthly series, with daily sources one
   * row per day while the chart shows them that way.
   */
  const productionSeries = useMemo(() => {
    if (!showDailyRows) return monthlyProductionSeries;

    return productionSources.flatMap((source) => {
      if (!isDailyProductionSource(source)) {
        return monthlyProductionSeries.filter(
          (series) => series.source === source
        );
      }
      if (dailyRecordsError) return [];

      return toProductionSeries(dailyRecords, dailyVersions, {
        source,
        namePrefix: productionSources.length > 1,
        isDailyRate: true,
      });
    });
  }, [
    showDailyRows,
    monthlyProductionSeries,
    productionSources,
    dailyRecords,
    dailyRecordsError,
    dailyVersions,
  ]);

  const forecastItems = useMemo(() => {
    if (!hasForecastSelection || bulkForecastsError) return [];

    const results = bulkForecasts?.results?.[uwi] ?? [];

    return results.map((item) => ({
      item,
      id: `forecast-${item.pdpForecastVersionId ?? item.wellDcaForecastId}`,
      name:
        selectedForecastVersions.find((version) =>
          sameId(version.versionId, item.pdpForecastVersionId)
        )?.label ??
        selectedInsightForecastVersions.find((version) =>
          sameId(version.versionId, item.wellDcaForecastId)
        )?.label ??
        '',
    }));
  }, [
    bulkForecasts,
    bulkForecastsError,
    hasForecastSelection,
    uwi,
    selectedForecastVersions,
    selectedInsightForecastVersions,
  ]);

  // The primary source's first version is the chart's production line, the one
  // the figures follow. If that source has nothing to draw, the line is left
  // empty rather than taken from another source. The other versions are drawn
  // as extra solid lines, and the forecasts as dashed ones.
  const primarySeries = useMemo(
    () => productionSeries.find((series) => series.source === primarySource),
    [productionSeries, primarySource]
  );
  const otherSeries = useMemo(
    () => productionSeries.filter((series) => series !== primarySeries),
    [productionSeries, primarySeries]
  );

  const chartProduction = useMemo(
    () => ({ ...EMPTY_SERIES, volume: primarySeries?.volume ?? [] }),
    [primarySeries]
  );

  const chartForecasts = useMemo<ForecastData[]>(
    () => [
      ...otherSeries.map((series) => ({
        ...EMPTY_SERIES,
        id: series.id,
        name: series.name,
        volume: series.volume,
        lineStyle: 'solid' as const,
        volumeIsDailyRate: series.isDailyRate,
      })),
      ...forecastItems.map(({ item, id, name }) => ({
        ...EMPTY_SERIES,
        id,
        name,
        volume: item.volumes,
      })),
    ],
    [otherSeries, forecastItems]
  );

  const handleStreamToggle = (stream: MineralType) => {
    setSelectedStreams((current) => {
      if (current.includes(stream)) {
        return current.filter((s) => s !== stream);
      }
      return [...current, stream];
    });
  };

  // ---------------------------------------------------------------------
  // Figures
  // ---------------------------------------------------------------------

  const forecastMetrics = useMemo(
    () =>
      forecastItems.map(({ item, name }) => ({
        label: name,
        // Insights forecasts carry a DCA forecast id; a legacy PDP one does not.
        isInsights:
          item.wellDcaForecastId !== null &&
          item.wellDcaForecastId !== undefined,
        metrics:
          (forecastMetricEntries.find((d) => {
            if (item.pdpForecastVersionId) {
              return sameId(d.pdpForecastVersionId, item.pdpForecastVersionId);
            }
            return sameId(d.wellDcaForecastId, item.wellDcaForecastId);
          })?.aggregationMetrics as AggregationMetrics | undefined) ?? null,
      })),
    [forecastItems, forecastMetricEntries]
  );

  // The Recovered figures for the primary source's first version, the one the
  // metric request asks for.
  const productionMetrics = useMemo(() => {
    const versionId = primaryVersions[0]?.versionId;
    if (versionId === undefined) return null;

    const entry = recordMetric?.pdpDeclines[uwi]?.find((d) =>
      sameId(d.productionVolumeVersionId, versionId)
    );

    return (
      (entry?.aggregationMetrics as AggregationMetrics | undefined) ?? null
    );
  }, [recordMetric, uwi, primaryVersions]);

  // ---------------------------------------------------------------------
  // DCA Forecast table
  // ---------------------------------------------------------------------

  // Per-foot EUR follows the producing wellbore's lateral, which the Wellbores
  // section has already read into this atom.
  const lateralLengthFt = useMemo(() => {
    const result = wellboresData.result;
    if (wellboresData.loading || result?.type !== 'success') return null;

    return (
      result.data.results.find(
        (wellbore) =>
          wellbore.uwi === uwi && wellbore.completionType === 'Producing'
      )?.perforatedLateralLengthFt ?? null
    );
  }, [wellboresData, uwi]);

  // The EUR card above this section shows the selected Insights forecast. A
  // legacy PDP forecast is not published here: it can differ enough that
  // showing it on that card would look like the wrong number.
  useEffect(() => {
    const insights = forecastMetrics.find((forecast) => forecast.isInsights);

    setSelectedForecastEur(
      forecastEurMetrics(insights?.metrics ?? null, lateralLengthFt)
    );
  }, [forecastMetrics, lateralLengthFt, setSelectedForecastEur]);

  // The table covers one forecast against one production version: the first of
  // each selected, which is also the chart's own production line.
  const tableForecast = forecastItems[0];

  const dcaRows = useMemo(
    () =>
      buildDcaForecastRows({
        productionVolumes:
          monthlyProductionSeries.find(
            (series) => series.source === primarySource
          )?.volume ?? [],
        forecastVolumes: tableForecast?.item.volumes ?? [],
        forecastStartDate:
          tableForecast?.item.declineStartDate ??
          tableForecast?.item.oilDeclineParams?.declineStartDate ??
          null,
      }),
    [monthlyProductionSeries, primarySource, tableForecast]
  );

  const exportFormValues = useMemo(
    () => forecastExportValues(tableForecast?.item ?? null),
    [tableForecast]
  );

  // The figures in the chart's title band, matching the DCA charts.
  const headerStats = useMemo(() => {
    const metrics = forecastMetrics.find((f) => f.isInsights)?.metrics;

    return [
      { label: 'Oil EUR', value: metrics?.oilEur, unit: 'bbl' },
      { label: 'Gas EUR', value: metrics?.gasEur, unit: 'mcf' },
      { label: 'Water EUR', value: metrics?.waterEur, unit: 'bbl' },
      { label: 'Lateral Length', value: lateralLengthFt, unit: 'ft' },
    ];
  }, [forecastMetrics, lateralLengthFt]);

  const chartProps = {
    production: chartProduction,
    productionVolumeIsDailyRate: primarySeries?.isDailyRate ?? false,
    forecasts: chartForecasts,
    isLoading,
    selectedStreams,
    onStreamToggle: handleStreamToggle,
    volumeDisplayMode,
    onVolumeDisplayModeChange: setVolumeDisplayMode,
    productionVersionLabels: productionSeries.map((series) => series.name),
    forecastVersionLabels: forecastItems.map(({ name }) => name),
    headerStats,
  };

  return (
    <>
      {(productionError || bulkForecastsError?.type) && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error occurred fetching volume"
        />
      )}

      <Tile className={style.chartTile}>
        <WellAnalysisChartPanel
          {...chartProps}
          height={SECTION_CHART_HEIGHT}
          controlsId="well-analysis-section-chart"
          headerActions={
            <div className={style.chartHeaderActions}>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Edit}
                iconDescription="Edit DCA Forecast"
                tooltipPosition="left"
                onClick={() => {
                  // Set before the editor renders, so it never loads with
                  // another scenario first.
                  if (dcaScenario) setSelectedDcaScenario(dcaScenario);
                  setDcaOpen(true);
                }}
              />
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Maximize}
                iconDescription="Expand chart"
                tooltipPosition="left"
                onClick={() => setChartExpanded(true)}
              />
            </div>
          }
        />
      </Tile>

      <Tile>
        <WellAnalysisEurSummary
          forecasts={forecastMetrics}
          productionMetrics={productionMetrics}
          isLoading={isLoading}
        />
      </Tile>

      <DeclineCurveAnalysisTable
        uwis={uwi}
        wellName={wellName}
        tableId="well-analysis-dca-forecast-table"
        rows={dcaRows}
        rowsLoading={isLoading}
        exportFormValues={exportFormValues}
      />

      <WellAnalysisChartTearsheet
        open={chartExpanded}
        onClose={() => setChartExpanded(false)}
        versionSelectors={versionSelectors}
        chart={{
          ...chartProps,
          controlsId: 'well-analysis-expanded-chart',
        }}
      />

      <WellDcaModal
        uwi={uwi}
        wellName={wellName}
        dcaForecastId={selectedInsightForecastVersions[0]?.versionId ?? null}
        open={dcaOpen}
        onClose={() => setDcaOpen(false)}
      />
    </>
  );
};
